/** Portal helpers: keep customer and professional auth flows separate. */

export const CUSTOMER_ROLE = 'customer';
export const PROFESSIONAL_ROLES = ['agent', 'broker', 'advisor'];

export const PORTAL = {
  CUSTOMER: 'customer',
  PROFESSIONAL: 'professional',
};

export function isProfessionalRole(role) {
  return PROFESSIONAL_ROLES.includes(String(role || '').toLowerCase());
}

export function normalizeRole(role) {
  const r = String(role || '').toLowerCase().trim();
  const aliases = {
    buyer: 'customer',
    renter: 'customer',
    seeker: 'customer',
    client: 'customer',
    property_agent: 'agent',
    real_estate_agent: 'agent',
    property_broker: 'broker',
    property_advisor: 'advisor',
    advisory: 'advisor',
    adviser: 'advisor',
  };
  const mapped = aliases[r] || r;
  if (mapped === CUSTOMER_ROLE || PROFESSIONAL_ROLES.includes(mapped)) {
    return mapped;
  }
  return CUSTOMER_ROLE;
}

/**
 * After login/signup, ensure the account belongs on this portal.
 * Returns { ok, message, redirect }.
 */
export function assertPortalAccess(accountRole, portal) {
  const role = normalizeRole(accountRole);

  if (portal === PORTAL.PROFESSIONAL) {
    if (!isProfessionalRole(role)) {
      return {
        ok: false,
        message:
          'This account is registered as a user (property seeker). Please use User Login, or create a Real Estate Agent account.',
        redirect: '/login',
      };
    }
    return {
      ok: true,
      message: null,
      redirect: '/agent-dashboard',
    };
  }

  // User portal
  if (isProfessionalRole(role)) {
    return {
      ok: true,
      message:
        'You are signed in with a Real Estate Agent account. Opening your agent dashboard.',
      redirect: '/agent-dashboard',
      isProfessionalOnCustomerPortal: true,
    };
  }

  return {
    ok: true,
    message: null,
    redirect: '/user-dashboard',
  };
}

export function buildAuthUserPayload({ apiUser = {}, firebaseUser = {}, fallback = {} }) {
  return {
    id: apiUser.id || fallback.id || firebaseUser.uid,
    name: apiUser.name || firebaseUser.displayName || fallback.name || '',
    email: apiUser.email || firebaseUser.email || fallback.email || '',
    avatar: firebaseUser.photoURL || apiUser.profile || fallback.avatar || '',
    account_role: normalizeRole(apiUser.account_role || fallback.account_role || CUSTOMER_ROLE),
    verification_status: apiUser.verification_status || fallback.verification_status || 'none',
    company_name: apiUser.company_name || fallback.company_name || '',
    joinDate: new Date().toISOString().split('T')[0],
    preferences: {
      notifications: true,
      newsletter: true,
    },
  };
}
